/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, January 26th 2022, 12:24:41 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useDrag, useDrop, DropTargetMonitor } from "react-dnd";
import { Identifier, XYCoord } from "dnd-core";
import { PrimaryKeyValue, ObjectValue } from "lib/types";

export interface UseDragDropListItemArgs<T> {
  itemType: Identifier;
  currentItemId: PrimaryKeyValue<T>;
  currentItemPosition: ObjectValue<T> & number;
  isDraggableItem?: boolean;
  handleDragEnter: (sourceItemId: PrimaryKeyValue<T>, hoverPosition: ObjectValue<T>) => void; // Callback for when the dragged item hovers over a valid drop target. https://developer.mozilla.org/en-US/docs/Web/API/Document/dragenter_event
  handleDrop: (
    sourceItemId: PrimaryKeyValue<T>,
    targetItemId: PrimaryKeyValue<T>,
    targetPosition: ObjectValue<T>
  ) => void; // Callback for when the dragged item is dropped to a valid drop target
}

export interface DraggedListItemState<T> {
  draggedItemId: PrimaryKeyValue<T>; // The primary key of the item being dragged.
  draggedItemInitialPosition: ObjectValue<T> & number; // This allows us to cancel the movement of items when the dragged item is dropped on an invalid target.
  draggedItemCurrentPosition: ObjectValue<T> & number; // This is purely for improving performance. We mutate it to halt the dragEnter callback once we have already moved items.
  dropTargetItemId: PrimaryKeyValue<T>; // Tracking the final dropTarget item id for the final drop callback action. This is incrementally updated as we hover items.
  dropTargetPosition: ObjectValue<T> & number; // Tracking the final dropTarget position for the final drop callback action. This is incrementally updated as we hover items.
}

interface ListItemCollectedDropProps {
  itemIsBeingDraggedOver: boolean;
}

interface ListItemCollectedDragProps {
  itemIsBeingDragged: boolean;
}

// https://codesandbox.io/s/github/react-dnd/react-dnd/tree/gh-pages/examples_hooks_ts/04-sortable/cancel-on-drop-outside

export const useDragDropListItem = <T>({
  itemType,
  currentItemId,
  currentItemPosition,
  handleDragEnter,
  handleDrop,
  isDraggableItem = true,
}: UseDragDropListItemArgs<T>) => {
  const dndListItemElRef = React.useRef<HTMLDivElement>(null); // Ref for the item participating in the drag and drop. Could be a dragged item or drop target

  const [{ itemIsBeingDragged }, dragSourceRef, dragPreviewRef] = useDrag<
    DraggedListItemState<T>,
    any,
    ListItemCollectedDragProps
  >(
    () => ({
      type: itemType,
      item: {
        draggedItemId: currentItemId,
        draggedItemInitialPosition: currentItemPosition,
        draggedItemCurrentPosition: currentItemPosition,
        dropTargetItemId: null,
        dropTargetPosition: null,
      },
      collect: (monitor) => ({
        itemIsBeingDragged: monitor.isDragging(),
      }),
      isDragging: (monitor) => monitor.getItem().draggedItemId === currentItemId,
      canDrag: (monitor) => isDraggableItem,
      end: (item, monitor) => {
        const didDrop = monitor.didDrop();
        if (!didDrop) {
          handleDragEnter(item.draggedItemId, item.draggedItemInitialPosition); // Cancel the move if not dropped.
        }
      },
    }),
    [currentItemId, currentItemPosition, handleDragEnter]
  );

  const [{ itemIsBeingDraggedOver }, dropTargetRef] = useDrop<
    DraggedListItemState<T>,
    void,
    ListItemCollectedDropProps
  >(
    () => ({
      accept: itemType,
      drop: (item, monitor) => {
        handleDrop(item.draggedItemId, item.dropTargetItemId, item.dropTargetPosition);
      },
      collect: (monitor) => ({
        itemIsBeingDraggedOver: !!monitor.isOver(),
      }),
      hover(item: DraggedListItemState<T>, monitor: DropTargetMonitor) {
        if (!dndListItemElRef.current || !isDraggableItem) {
          return;
        }

        const draggedItemPosition = item.draggedItemCurrentPosition;
        const draggedItemId = item.draggedItemId;
        const hoveredItemPosition = currentItemPosition;
        const hoveredItemId = currentItemId;

        // Don't replace items with themselves
        if (draggedItemId === hoveredItemId) {
          return;
        }

        // Determine rectangle on screen for the hovered item
        const hoverBoundingRect = dndListItemElRef.current?.getBoundingClientRect();

        // Get vertical middle of the hovered item
        const hoverItemMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        // Determine cursor/mouse position as the drag proceeds (For touch screen, the user's finger is the cursor)
        const clientOffset = monitor.getClientOffset();

        // Get pixels to the top
        const hoverCursorPositionY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        // Dragging downwards
        if (draggedItemPosition < hoveredItemPosition && hoverCursorPositionY < hoverItemMiddleY) {
          return;
        }

        // Dragging upwards
        if (draggedItemPosition > hoveredItemPosition && hoverCursorPositionY > hoverItemMiddleY) {
          return;
        }

        // When the cursor intersects with the vertical middle of the hovered item (i.e hoverCursorPositionY === hoverItemMiddleY),
        // our dragged item has entered the valid drop target and its time to actually perform the action
        handleDragEnter(draggedItemId, hoveredItemPosition);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        item.draggedItemCurrentPosition = hoveredItemPosition;

        // Again, doing a mutation here so we can track the actual drop target.
        // TODO: See if react-dnd has any way of tracking this in state
        item.dropTargetItemId = hoveredItemId;
        item.dropTargetPosition = hoveredItemPosition;
      },
    }),
    [handleDragEnter, handleDrop]
  );

  dragPreviewRef(getEmptyImage()); // Feeding the browser an empty image so we can have a custom drag preview via the drag layer component - https://react-dnd.github.io/react-dnd/examples/drag-around/custom-drag-layer
  dragSourceRef(dropTargetRef(dndListItemElRef));

  return { dndListItemElRef, itemIsBeingDragged, itemIsBeingDraggedOver };
};
